import {
  DUE_WINDOW_LABELS,
  DUE_WINDOWS,
  dueWindow,
  RECORD_ENTITIES,
  RECORD_ENTITY_LABELS,
  TASK_STATUS_LABELS,
  TASK_STATUSES,
  weekBounds,
  type DueWindow,
  type MyTasksFilter,
  type Permission,
  type RecordEntity,
  type TaskStatus,
  type TaskType,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheckBig, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { LayoutToggle } from '../components/boards/layout-toggle.tsx';
import { MY_TASKS_QUERY, TaskRow, tasksQuery } from '../components/records/record-sidebar.tsx';
import { TaskBoard } from '../components/tasks/task-board.tsx';
import { Gate } from '../components/shell/gate.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '../components/ui/dropdown-menu.tsx';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { describeError } from '../lib/api.ts';
import { api, type TaskView } from '../lib/client.ts';
import { useMembers } from '../lib/record-data.ts';
import { useSession } from '../lib/session.tsx';
import { useSettings } from '../lib/settings.ts';
import { TaskFilterChip } from '../components/tasks/task-filter-chip.tsx';
import { showsCalendar } from '../components/home/calendar-access.ts';
import { CalendarCard } from '../components/home/calendar-card.tsx';
import { BlockCard } from '../components/records/block-card.tsx';
import { cn } from '../lib/utils.ts';
import { TaskGroup } from '../components/tasks/task-group.tsx';
import {
  automatedFor,
  TaskKindChip,
  TaskTypeChip,
  typesFor,
  type TaskKind,
} from '../components/tasks/task-kind-select.tsx';

export const TEAM_TASKS_QUERY = ['tasks', 'team'] as const;

/** The record permission that lets someone other than the assignee change a task's status. */
const WRITE_PERMISSION: Record<RecordEntity, Permission> = {
  candidates: 'candidate.update',
  companies: 'customer.update',
  contacts: 'customer.update',
  projects: 'customer.update',
  deals: 'customer.update',
  jobs: 'job.update',
};

const STATUS_OPTIONS: ReadonlyArray<{ value: MyTasksFilter; label: string }> = [
  { value: 'open', label: 'Not done' },
  ...TASK_STATUSES.map((s) => ({ value: s, label: TASK_STATUS_LABELS[s] })),
  { value: 'all', label: 'Any status' },
];

/**
 * Team Tasks (TS-1743): the tasks of everyone the caller's `task.read` reaches — the
 * organisation for Manager and Admin until teams can be assigned, a team's members under a
 * `team` grant — grouped by when they are due (the same New York week as the API's `due`
 * filter). Filters are server-side. Changing a status is the record sidebar's PATCH: the
 * assignee, or anyone who can write to the record, may move it.
 *
 * The Board layout (TS-1744) is My Tasks' board with the assignee on each card: a column per
 * status, each one `/tasks/team` with these filters and that status (so the Status filter is
 * hidden there), and a drag is the same PATCH under the same rule. The layout is remembered
 * per user. Each column has the endpoint's 500 cap, not per-column paging.
 */
export function TeamTasksPage() {
  return (
    <Gate permission="task.read">
      <TeamTasks />
    </Gate>
  );
}

function TeamTasks() {
  const qc = useQueryClient();
  const session = useSession();
  const showCalendar = showsCalendar(session);
  const [settings, updateSettings] = useSettings(session.me.user.id);
  const layout = settings.teamTasksLayout;
  const board = layout === 'board';
  const { members } = useMembers();
  const teams = useQuery({
    queryKey: ['teams', session.me.organisation.id],
    queryFn: api.teams.list,
    enabled: session.can('team.read'),
  });
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [status, setStatus] = useState<MyTasksFilter>('open');
  const [due, setDue] = useState<DueWindow | undefined>();
  const [entityType, setEntityType] = useState<RecordEntity | undefined>();
  const [type, setType] = useState<TaskType | undefined>();
  const [kind, setKind] = useState<TaskKind>('manual');
  // A type the new kind cannot have would filter to nothing, so it is dropped.
  const changeKind = (next: TaskKind) => {
    setKind(next);
    if (type && !typesFor(next).includes(type)) setType(undefined);
  };

  // The API resolves teams to their members (`teamIds`); here they only narrow the people menu.
  // A deleted team drops out of the choice, and people outside the chosen teams are ignored.
  const chosenTeams = (teams.data ?? []).filter((t) => teamIds.includes(t.id));
  const inTeams = chosenTeams.length
    ? new Set(chosenTeams.flatMap((t) => t.members.map((m) => m.userId)))
    : null;
  const chosen = inTeams ? assigneeIds.filter((id) => inTeams.has(id)) : assigneeIds;
  // Changing teams keeps only the people still shown, so someone hidden by a team never
  // comes back selected when the team is unticked.
  const pickTeams = (next: string[]) => {
    setAssigneeIds(chosen);
    setTeamIds(next);
  };
  const others = {
    teamIds: chosenTeams.map((t) => t.id),
    assigneeIds: chosen,
    status,
    due,
    entityType,
    automated: automatedFor(kind),
  };
  const filters = { ...others, type };
  // The people the three cards are about (empty = everyone the caller reaches).
  const scope = { teamIds: filters.teamIds, assigneeIds: filters.assigneeIds };
  const tasks = useQuery({
    queryKey: [...TEAM_TASKS_QUERY, filters],
    queryFn: () => api.tasks.team(filters),
    // Only the list reads this query: the board has its own per-column reads.
    enabled: !board,
  });
  const typeCounts = useQuery({
    queryKey: [...TEAM_TASKS_QUERY, 'type-counts', others],
    queryFn: () => api.tasks.teamTypeCounts(others),
  });
  const [error, setError] = useState<string | null>(null);
  const changeStatus = useMutation({
    mutationFn: ({ task, status }: { task: TaskView; status: TaskStatus }) =>
      api.tasks.patch(task.id, { status }),
    onSuccess: async (t) => {
      setError(null);
      await qc.invalidateQueries({ queryKey: TEAM_TASKS_QUERY });
      await qc.invalidateQueries({ queryKey: MY_TASKS_QUERY });
      await qc.invalidateQueries({ queryKey: tasksQuery(t.entityId, t.entityType) });
    },
    onError: (err) => setError(describeError(err)),
  });

  // The people the caller's scope reaches: `can()` over the member as if it were a record
  // they own, which is exactly how the API reads a `task.read` grant.
  const people = members.filter(
    (m) =>
      session.can('task.read', { ownerId: m.id, teamId: m.teamId }) &&
      (!inTeams || inTeams.has(m.id)),
  );
  const userId = session.me.user.id;
  const bounds = weekBounds();
  const items = board ? [] : (tasks.data ?? []);
  const groups: Array<{ name: string; items: TaskView[] }> =
    status === 'done'
      ? [{ name: 'Completed', items }]
      : DUE_WINDOWS.map((w) => ({
          name: DUE_WINDOW_LABELS[w],
          items: items.filter((t) => dueWindow(t.dueAt, bounds) === w),
        }));
  // On the board the columns are the statuses, so the Status filter does not apply there.
  const filtered =
    chosenTeams.length > 0 ||
    chosen.length > 0 ||
    (!board && status !== 'open') ||
    !!due ||
    !!entityType ||
    !!type ||
    kind !== 'manual';
  const canMove = (t: TaskView) =>
    t.assignee?.id === userId || session.can(WRITE_PERMISSION[t.entityType]);

  return (
    <>
      <PageHeader
        title="Team Tasks"
        description="The team's tasks and meetings, for everyone or one person."
      />
      {/* Who: drives the three cards. */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="team-scope-filters">
        {!!teams.data?.length && (
          <TaskFilterChip
            name="Team"
            label={
              chosenTeams.length === 0
                ? 'Team'
                : chosenTeams.length === 1
                  ? chosenTeams[0]!.name
                  : `${chosenTeams.length} teams`
            }
            active={chosenTeams.length > 0}
            onClear={() => pickTeams([])}
            testId="filter-team"
          >
            {(match) =>
              teams.data
                .filter((t) => match(t.name))
                .map((t) => (
                  <DropdownMenuCheckboxItem
                    key={t.id}
                    checked={teamIds.includes(t.id)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(on) =>
                      pickTeams(on ? [...teamIds, t.id] : teamIds.filter((id) => id !== t.id))
                    }
                  >
                    {t.name}
                  </DropdownMenuCheckboxItem>
                ))
            }
          </TaskFilterChip>
        )}
        <TaskFilterChip
          name="Assignee"
          label={
            chosen.length === 0
              ? 'Assignee'
              : chosen.length === 1
                ? (people.find((p) => p.id === chosen[0])?.name ?? '1 person')
                : `${chosen.length} people`
          }
          active={chosen.length > 0}
          onClear={() => setAssigneeIds([])}
          testId="filter-assignee"
        >
          {(match) =>
            people
              .filter((p) => match(p.name))
              .map((p) => (
                <DropdownMenuCheckboxItem
                  key={p.id}
                  checked={chosen.includes(p.id)}
                  // Keep the menu open: picking several people is the usual case.
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(on) =>
                    setAssigneeIds((ids) => (on ? [...ids, p.id] : ids.filter((id) => id !== p.id)))
                  }
                >
                  {p.name}
                  {p.id === userId && ' (you)'}
                  {p.status === 'deactivated' && ' · deactivated'}
                </DropdownMenuCheckboxItem>
              ))
          }
        </TaskFilterChip>
        {filtered && (
          <Button
            size="sm"
            variant="link"
            data-testid="clear-filters"
            onClick={() => {
              setTeamIds([]);
              setAssigneeIds([]);
              setStatus('open');
              setDue(undefined);
              setEntityType(undefined);
              setType(undefined);
              setKind('manual');
            }}
          >
            Clear filters
          </Button>
        )}
      </div>
      {/* The viewport less the header, the people filters and the page padding: no page scroll. */}
      <div
        className={cn(
          'grid grid-cols-1 gap-4 lg:h-[calc(100dvh-13.5rem)]',
          showCalendar && 'lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]',
        )}
      >
        {/* Tasks takes the whole height; its list scrolls inside. */}
        <div className="min-h-0">
          <BlockCard
            title="Tasks"
            count={board ? undefined : tasks.data?.length}
            fill
            toolbar={
              <div className="flex flex-wrap items-center gap-2" data-testid="team-task-filters">
                {!board && (
                  <TaskFilterChip
                    name="Status"
                    label={STATUS_OPTIONS.find((o) => o.value === status)!.label}
                    active={status !== 'open'}
                    onClear={() => setStatus('open')}
                    testId="filter-status"
                  >
                    {(match) => (
                      <DropdownMenuRadioGroup
                        value={status}
                        onValueChange={(v) => setStatus(v as MyTasksFilter)}
                      >
                        {STATUS_OPTIONS.filter((o) => match(o.label)).map((o) => (
                          <DropdownMenuRadioItem key={o.value} value={o.value}>
                            {o.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    )}
                  </TaskFilterChip>
                )}
                <TaskFilterChip
                  name="Due"
                  label={due ? DUE_WINDOW_LABELS[due] : 'Due'}
                  active={!!due}
                  onClear={() => setDue(undefined)}
                  testId="filter-due"
                >
                  {(match) => (
                    <DropdownMenuRadioGroup
                      value={due ?? ''}
                      onValueChange={(v) => setDue(v as DueWindow)}
                    >
                      {DUE_WINDOWS.filter((w) => match(DUE_WINDOW_LABELS[w])).map((w) => (
                        <DropdownMenuRadioItem key={w} value={w}>
                          {DUE_WINDOW_LABELS[w]}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  )}
                </TaskFilterChip>
                <TaskFilterChip
                  name="Record"
                  label={entityType ? RECORD_ENTITY_LABELS[entityType] : 'Record'}
                  active={!!entityType}
                  onClear={() => setEntityType(undefined)}
                  testId="filter-record"
                >
                  {(match) => (
                    <DropdownMenuRadioGroup
                      value={entityType ?? ''}
                      onValueChange={(v) => setEntityType(v as RecordEntity)}
                    >
                      {RECORD_ENTITIES.filter((e) => match(RECORD_ENTITY_LABELS[e])).map((e) => (
                        <DropdownMenuRadioItem key={e} value={e}>
                          {RECORD_ENTITY_LABELS[e]}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  )}
                </TaskFilterChip>
                <TaskTypeChip kind={kind} type={type} onChange={setType} counts={typeCounts.data} />
                <TaskKindChip kind={kind} onChange={changeKind} />
                <LayoutToggle
                  layout={layout}
                  tableLabel="List"
                  onChange={(next) =>
                    updateSettings({ teamTasksLayout: next === 'board' ? 'board' : 'table' })
                  }
                />
              </div>
            }
          >
            {() => (
              <>
                {error && (
                  <p className="text-danger mb-3 text-sm" role="alert">
                    {error}
                  </p>
                )}
                {board && (
                  <TaskBoard
                    name="team-tasks"
                    scope={[others.teamIds, chosen, due, entityType, type, others.automated]}
                    load={(s) => api.tasks.team({ ...filters, status: s })}
                    canMove={canMove}
                    canDelete={(t) => t.createdBy?.id === userId}
                    showAssignee
                  />
                )}
                {!board && tasks.isPending && (
                  <div className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Loader2 className="size-4 animate-spin" /> Loading…
                  </div>
                )}
                {!board && tasks.error != null && (
                  <p className="text-danger text-sm" role="alert">
                    {describeError(tasks.error)}
                  </p>
                )}
                {!board && tasks.data?.length === 0 && (
                  <EmptyState
                    icon={CircleCheckBig}
                    data-testid="no-tasks"
                    className="h-full"
                    title={filtered ? 'No tasks match these filters.' : 'Nothing open on the team.'}
                    description={
                      filtered
                        ? 'Try another team, person, status, due date or type.'
                        : 'Tasks assigned to anyone on the team show up here.'
                    }
                  />
                )}
                {items.length === 500 && (
                  <p className="text-muted-foreground mb-3 text-xs" data-testid="team-tasks-capped">
                    Showing the first 500 tasks. Narrow the filters to see the rest.
                  </p>
                )}
                <div className="flex flex-col gap-3">
                  {groups
                    .filter((g) => g.items.length > 0)
                    .map((g) => (
                      <TaskGroup key={g.name} name={g.name} count={g.items.length}>
                        {g.items.map((t) => (
                          <TaskRow
                            key={t.id}
                            task={t}
                            onStatusChange={(next) =>
                              changeStatus.mutate({ task: t, status: next })
                            }
                            canToggle={canMove(t)}
                            canDelete={t.createdBy?.id === userId}
                            showRecord
                            showAssignee
                          />
                        ))}
                      </TaskGroup>
                    ))}
                </div>
              </>
            )}
          </BlockCard>
        </div>
        {showCalendar && (
          <div className="min-h-0">
            <CalendarCard team={scope} fill />
          </div>
        )}
      </div>
    </>
  );
}
